import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  FileText,
  User,
  Building,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  Truck,
  Download,
  Eye,
  Ship,
  FileSignature,
  RefreshCw,
  FileSpreadsheet,
  Globe,
  AlertCircle,
  Copy,
  Link as LinkIcon,
} from 'lucide-react';

import CustomButton from '../../components/ui/CustomButton';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../components/common/Notification';
import { certificatesService } from '../../services/certificates';
import DocumentsTab from './DocumentTab';
import PDFViewer from '../../components/common/PdfViewer';
import DraftToProductionModal from '../../components/common/DratfToProdModal';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`certificate-tabpanel-${index}`}
      aria-labelledby={`certificate-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, children, isDarkMode }) => (
  <Card
    sx={{
      mb: 3,
      background: isDarkMode
        ? 'rgba(30, 41, 59, 0.6)'
        : 'rgba(255, 255, 255, 0.8)',
      border: isDarkMode
        ? '1px solid rgba(148, 163, 184, 0.1)'
        : '1px solid rgba(148, 163, 184, 0.2)',
      borderRadius: 2,
      backdropFilter: 'blur(10px)',
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Icon size={20} color="#3b82f6" />
        <Typography
          variant="h6"
          sx={{
            ml: 1,
            fontWeight: 600,
            color: isDarkMode ? '#f1f5f9' : '#1e293b',
          }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </Card>
);

const InfoRow = ({ label, value, isDarkMode, highlight = false }) => {
  const isReactElement = React.isValidElement(value);
  
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={4}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: isDarkMode ? '#cbd5e1' : '#475569',
          }}
        >
          {label}:
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        {isReactElement ? (
          value
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: highlight ? '#3b82f6' : isDarkMode ? '#e2e8f0' : '#1e293b',
              fontWeight: highlight ? 600 : 400,
              wordBreak: 'break-word',
            }}
          >
            {value || 'Not specified'}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

const ViewCertificateDialog = ({
  open,
  onClose,
  certificate,
  isDarkMode,
  onRefresh,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { isAdmin, isOffice } = useAuth();
  const { showNotification } = useNotification();
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [isReissuing, setIsReissuing] = useState(false);
  const [reissueDialogOpen, setReissueDialogOpen] = useState(false);

  if (!certificate) return null;

  const isDraft = certificate.IsDraft;
  const isCompleted = !isDraft && certificate.IsStage1Sync && certificate.IsStage2Sync;
  const isReCertificate = certificate.IsReCertificate === true;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle draft to production conversion
  const handleConvertToProduction = async () => {
    if (!isOffice()) {
      showNotification('Only office users can convert certificates', 'error');
      return;
    }

    setConvertModalOpen(true);
  };

  // Handle the actual conversion from the modal
  const handleModalConversion = async (issuanceDate, expiryDate) => {
    try {
      await certificatesService.changeDraftStatus(certificate.Id, {
        issuanceDate,
        expiryDate,
      });

      showNotification(
        'Certificate converted to production successfully! COC Number has been assigned.',
        'success'
      );

      // Refresh the certificate data and close modal
      if (onRefresh) {
        onRefresh();
      }
      setConvertModalOpen(false);
      onClose(); // Close the view dialog as well since certificate changed
    } catch (error) {
      const errorMessage =
        error?.message || error?.toString() || 'Unknown error';
      showNotification(
        `Failed to convert certificate: ${errorMessage}`,
        'error'
      );
      // Don't close modal on error so user can retry
      throw error; // Re-throw so modal handles it
    }
  };

  // Handle modal close
  const handleCloseConvertModal = () => {
    setConvertModalOpen(false);
  };

  // Handle certificate reissue - open confirmation dialog
  const handleReissue = () => {
    if (!isOffice() && !isAdmin()) {
      showNotification('Only office users and admins can reissue certificates', 'error');
      return;
    }

    if (!isCompleted) {
      showNotification('Only completed certificates can be reissued', 'warning');
      return;
    }

    // Open confirmation dialog
    setReissueDialogOpen(true);
  };

  // Handle confirmed reissue
  const handleConfirmReissue = async () => {
    setIsReissuing(true);
    try {
      const reissuedCertificate = await certificatesService.reissueCertificate(certificate.Id);
      showNotification(
        `Certificate reissued successfully! New COC Number: ${reissuedCertificate.CocNumber}`,
        'success'
      );
      
      setReissueDialogOpen(false);
      
      // Refresh the certificate data if onRefresh is provided
      if (onRefresh) {
        onRefresh();
      }
      
      // Close the dialog to show the new certificate
      onClose();
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || 'Failed to reissue certificate';
      showNotification(errorMessage, 'error');
    } finally {
      setIsReissuing(false);
    }
  };

  const handleCloseReissueDialog = () => {
    if (!isReissuing) {
      setReissueDialogOpen(false);
    }
  };

  // Handle PDF download
  const handleDownload = async () => {
    try {
      const blob = await certificatesService.getCertificatePDF(
        certificate.Id,
        isDraft
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Create appropriate filename
      const baseFileName =
        certificate.CocNumber || `certificate-${certificate.Id}`;
      const suffix = isDraft ? '_draft' : '';
      link.download = `${baseFileName}${suffix}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification(
        `Certificate PDF downloaded successfully${isDraft ? ' (Draft Version)' : ''}`,
        'success'
      );
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download certificate PDF', 'error');
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
          maxHeight: '95vh',
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileText size={28} color="#06b6d4" />
            <Box sx={{ ml: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  }}
                >
                  {certificate.CocNumber || `Draft-${certificate.Id}`}
                </Typography>

                {/* Draft Status Indicator */}
                {isDraft && (
                  <Chip
                    label="📝 DRAFT"
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      background:
                        'linear-gradient(135deg, #f59e0b20, #f59e0b10)',
                      color: '#f59e0b',
                      border: '1px solid #f59e0b30',
                      fontWeight: 600,
                    }}
                  />
                )}
                
                {/* Reissued Certificate Badge */}
                {isReCertificate && (
                  <Chip
                    label="🔄 REISSUED"
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      background:
                        'linear-gradient(135deg, #8b5cf620, #8b5cf610)',
                      color: '#8b5cf6',
                      border: '1px solid #8b5cf630',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
              >
                {/* Sync Status Chips for Admin */}
                {isAdmin() && !isDraft && (
                  <>
                    <Chip
                      label={
                        certificate.IsStage1Sync ? 'Stage 1 ✓' : 'Stage 1 ✗'
                      }
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        background: certificate.IsStage1Sync
                          ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                          : 'linear-gradient(135deg, #ef444420, #ef444410)',
                        color: certificate.IsStage1Sync ? '#10b981' : '#ef4444',
                        border: `1px solid ${certificate.IsStage1Sync ? '#10b98130' : '#ef444430'}`,
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={
                        certificate.IsStage2Sync ? 'Stage 2 ✓' : 'Stage 2 ✗'
                      }
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        background: certificate.IsStage2Sync
                          ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                          : 'linear-gradient(135deg, #ef444420, #ef444410)',
                        color: certificate.IsStage2Sync ? '#10b981' : '#ef4444',
                        border: `1px solid ${certificate.IsStage2Sync ? '#10b98130' : '#ef444430'}`,
                        fontWeight: 600,
                      }}
                    />
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Convert to Production Button for Drafts */}
          {isDraft && isOffice() && (
            <CustomButton
              variant="gradient"
              gradient="success"
              onClick={handleConvertToProduction}
              startIcon={<RefreshCw size={16} />}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  boxShadow: '0 8px 25px rgba(16, 185, 129, 0.6)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Convert to Production
            </CustomButton>
          )}

          {/* Reissue Certificate Button for Completed Certificates */}
          {isCompleted && (isOffice() || isAdmin()) && (
            <CustomButton
              variant="gradient"
              gradient="primary"
              onClick={handleReissue}
              disabled={isReissuing}
              startIcon={isReissuing ? <RefreshCw size={16} className="animate-spin" /> : <Copy size={16} />}
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  opacity: 0.6,
                  transform: 'none',
                },
              }}
            >
              {isReissuing ? 'Reissuing...' : 'Reissue Certificate'}
            </CustomButton>
          )}
        </Box>

        {/* Draft Information Alert */}
        {isDraft && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              background: isDarkMode
                ? 'rgba(251, 146, 60, 0.1)'
                : 'rgba(251, 146, 60, 0.05)',
              border: '1px solid rgba(251, 146, 60, 0.2)',
            }}
          >
            <Typography variant="body2">
              <strong>Draft Certificate:</strong> This certificate is in draft
              mode. Convert to production to assign an official COC number and
              make it active.
            </Typography>
          </Alert>
        )}

        {/* Reissued Certificate Information Alert */}
        {isReCertificate && certificate.OriginalCocNumber && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              background: isDarkMode
                ? 'rgba(139, 92, 246, 0.1)'
                : 'rgba(139, 92, 246, 0.05)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
            }}
          >
            <Typography variant="body2">
              <strong>Reissued Certificate:</strong> This certificate is a reissue of{' '}
              <strong>{certificate.OriginalCocNumber}</strong>. All data has been copied from the original certificate and can be modified.
            </Typography>
          </Alert>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="certificate details tabs"
            sx={{
              '& .MuiTab-root': {
                color: isDarkMode ? '#94a3b8' : '#64748b',
                '&.Mui-selected': {
                  color: '#3b82f6',
                },
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
          >
            <Tab
              label="General Information"
              icon={<FileText size={16} />}
              iconPosition="start"
            />
            <Tab
              label="Parties"
              icon={<User size={16} />}
              iconPosition="start"
            />
            <Tab
              label="Invoice & Shipment"
              icon={<DollarSign size={16} />}
              iconPosition="start"
            />
            <Tab
              label="Origin & Transport"
              icon={<Ship size={16} />}
              iconPosition="start"
            />
            <Tab
              label="Certificate Items"
              icon={<Package size={16} />}
              iconPosition="start"
            />
            <Tab
              label="PDF Viewer"
              icon={<Eye size={16} />}
              iconPosition="start"
            />
            <Tab
              label="Documents"
              icon={<FileSpreadsheet size={16} />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
          {/* General Information Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={FileText}
                  title="Certificate Details"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="COC Number"
                    value={certificate.CocNumber}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  {isReCertificate && certificate.OriginalCocNumber && (
                    <InfoRow
                      label="Original COC Number"
                      value={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinkIcon size={14} color="#8b5cf6" />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#8b5cf6',
                              fontWeight: 600,
                            }}
                          >
                            {certificate.OriginalCocNumber}
                          </Typography>
                        </Box>
                      }
                      isDarkMode={isDarkMode}
                    />
                  )}
                  <InfoRow
                    label="Registration Number"
                    value={certificate.RegistrationNumber}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Reference Number"
                    value={certificate.ReferenceNumber}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Issuer Name"
                    value={certificate.IssuerName}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Issuing Office"
                    value={certificate.IssuingOffice}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Branch"
                    value={certificate.Branch}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Incoterms"
                    value={certificate.Incoterms}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Signee"
                    value={certificate.Signee}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>

              {!isDraft && (
                <Grid item xs={12} md={6}>
                  <InfoCard
                    icon={Calendar}
                    title="Important Dates"
                    isDarkMode={isDarkMode}
                  >
                    <InfoRow
                      label="Issuance Date"
                      value={formatDate(certificate.IssuanceDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="Expiry Date"
                      value={formatDate(certificate.ExpiryDate)}
                      isDarkMode={isDarkMode}
                      highlight
                    />
                    <InfoRow
                      label="Assessment Date"
                      value={formatDate(certificate.AssessmentDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="Invoice Date"
                      value={formatDate(certificate.InvoiceDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="Document Date"
                      value={formatDate(certificate.DocumentDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="BL Date"
                      value={formatDate(certificate.BlDate)}
                      isDarkMode={isDarkMode}
                    />
                  </InfoCard>
                </Grid>
              )}

              {isDraft && (
                <Grid item xs={12} md={6}>
                  <InfoCard
                    icon={Calendar}
                    title="Important Dates"
                    isDarkMode={isDarkMode}
                  >
                    <InfoRow
                      label="Assessment Date"
                      value={formatDate(certificate.AssessmentDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="Invoice Date"
                      value={formatDate(certificate.InvoiceDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="Document Date"
                      value={formatDate(certificate.DocumentDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="BL Date"
                      value={formatDate(certificate.BlDate)}
                      isDarkMode={isDarkMode}
                    />
                    <InfoRow
                      label="Created Date"
                      value={formatDate(certificate.CreatedAt)}
                      isDarkMode={isDarkMode}
                      highlight
                    />
                  </InfoCard>
                </Grid>
              )}

              {/* Sync Status Section for Admin */}
              {isAdmin() && !isDraft && (
                <Grid item xs={12}>
                  <InfoCard
                    icon={FileSignature}
                    title="Sync Status (Admin Only)"
                    isDarkMode={isDarkMode}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDarkMode ? '#cbd5e1' : '#475569',
                            }}
                          >
                            Stage 1 Sync:
                          </Typography>
                          <Chip
                            label={
                              certificate.IsStage1Sync ? 'Synced' : 'Not Synced'
                            }
                            size="small"
                            sx={{
                              background: certificate.IsStage1Sync
                                ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                                : 'linear-gradient(135deg, #ef444420, #ef444410)',
                              color: certificate.IsStage1Sync
                                ? '#10b981'
                                : '#ef4444',
                              border: `1px solid ${certificate.IsStage1Sync ? '#10b98130' : '#ef444430'}`,
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDarkMode ? '#cbd5e1' : '#475569',
                            }}
                          >
                            Stage 2 Sync:
                          </Typography>
                          <Chip
                            label={
                              certificate.IsStage2Sync ? 'Synced' : 'Not Synced'
                            }
                            size="small"
                            sx={{
                              background: certificate.IsStage2Sync
                                ? 'linear-gradient(135deg, #10b98120, #10b98110)'
                                : 'linear-gradient(135deg, #ef444420, #ef444410)',
                              color: certificate.IsStage2Sync
                                ? '#10b981'
                                : '#ef4444',
                              border: `1px solid ${certificate.IsStage2Sync ? '#10b98130' : '#ef444430'}`,
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </InfoCard>
                </Grid>
              )}

              {certificate.Remarks && (
                <Grid item xs={12}>
                  <InfoCard
                    icon={AlertCircle}
                    title="Remarks"
                    isDarkMode={isDarkMode}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDarkMode ? '#e2e8f0' : '#1e293b',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {certificate.Remarks}
                    </Typography>
                  </InfoCard>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* Parties Tab */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={User}
                  title="Importer Information"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="Name"
                    value={certificate.ImporterName}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  <InfoRow
                    label="Address"
                    value={certificate.ImporterAddress}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="City"
                    value={certificate.ImporterCity}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Country"
                    value={certificate.ImporterCountry}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={Building}
                  title="Exporter Information"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="Name"
                    value={certificate.ExporterName}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  <InfoRow
                    label="Address"
                    value={certificate.ExporterAddress}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Country"
                    value={certificate.ExporterCountry}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Invoice & Shipment Tab */}
          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={DollarSign}
                  title="Invoice Information"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="Invoice Value (Fee Basis)"
                    value={certificate.InvoiceValueFeeBasis}
                    isDarkMode={isDarkMode}
                    highlight
                  />

                  <InfoRow
                    label="Invoice Number"
                    value={certificate.InvoiceNumber}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  <InfoRow
                    label="Invoice Date"
                    value={formatDate(certificate.InvoiceDate)}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Invoice Value"
                    value={formatCurrency(
                      certificate.InvoiceValue,
                      certificate.InvoiceCurrency
                    )}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  <InfoRow
                    label="Currency"
                    value={certificate.InvoiceCurrency}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Declared Value/Container"
                    value={formatCurrency(
                      certificate.DeclaredValuePerContainer,
                      certificate.InvoiceCurrency
                    )}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={Truck}
                  title="Shipment Information"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="Shipment Method"
                    value={certificate.ShipmentMethod}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Document Number"
                    value={certificate.DocumentNumber}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Country of Shipment"
                    value={certificate.CountryOfShipment}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Point of Entry"
                    value={certificate.PointOfEntry}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Container Seal Number"
                    value={certificate.ContainerSealNumber}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>

              <Grid item xs={12}>
                <InfoCard
                  icon={Package}
                  title="Packaging & Container Details"
                  isDarkMode={isDarkMode}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <InfoRow
                        label="Packing Details"
                        value={certificate.PackingDetails}
                        isDarkMode={isDarkMode}
                      />
                      <InfoRow
                        label="Container Details"
                        value={certificate.ContainerDetails}
                        isDarkMode={isDarkMode}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoRow
                        label="Number & Type of Package"
                        value={certificate.NumberAndTypeOfPackage}
                        isDarkMode={isDarkMode}
                      />
                    </Grid>
                  </Grid>
                </InfoCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Origin & Transport Tab */}
          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={Ship}
                  title="Transport Information"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="Shipment Type"
                    value={certificate.ShipmentType}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  <InfoRow
                    label="BL Number"
                    value={certificate.BlNo}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="BL Date"
                    value={formatDate(certificate.BlDate)}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    label="Containers Count"
                    value={certificate.ContainersCount}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoCard
                  icon={Globe}
                  title="Origin Information"
                  isDarkMode={isDarkMode}
                >
                  <InfoRow
                    label="Country of Origin"
                    value={certificate.CountryOrigin}
                    isDarkMode={isDarkMode}
                    highlight
                  />
                  <InfoRow
                    label="Branch"
                    value={certificate.Branch}
                    isDarkMode={isDarkMode}
                  />
                </InfoCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Certificate Items Tab */}
          <TabPanel value={activeTab} index={4}>
            {certificate.Items && certificate.Items.length > 0 ? (
              <TableContainer
                component={Paper}
                sx={{
                  background: isDarkMode
                    ? 'rgba(30, 41, 59, 0.6)'
                    : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: isDarkMode
                    ? '1px solid rgba(148, 163, 184, 0.1)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Description
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Quantity
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Origin
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Value
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Standard Ref
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                        }}
                      >
                        Brand
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {certificate.Items.map((item, index) => (
                      <TableRow key={index}>
                        {/* UPDATED: Item Type Column - Separate from Description */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          {item.Type ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {/* Add type icons */}
                              {item.Type === 'Textile' && (
                                <span style={{ marginRight: 8 }}>🧵</span>
                              )}
                              {item.Type === 'Construction' && (
                                <span style={{ marginRight: 8 }}>🏗️</span>
                              )}
                              {item.Type === 'Safety' && (
                                <span style={{ marginRight: 8 }}>🦺</span>
                              )}
                              {item.Type === 'Food' && (
                                <span style={{ marginRight: 8 }}>🍎</span>
                              )}
                              {item.Type === 'Chemical' && (
                                <span style={{ marginRight: 8 }}>⚗️</span>
                              )}
                              {item.Type === 'Engineering' && (
                                <span style={{ marginRight: 8 }}>⚙️</span>
                              )}
                              <Chip
                                label={item.Type}
                                size="small"
                                sx={{
                                  background:
                                    'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                fontStyle: 'italic',
                              }}
                            >
                              Not specified
                            </Typography>
                          )}
                        </TableCell>

                        {/* UPDATED: Item Description Column - Separate with Production Date */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.ItemDescription || 'Not specified'}
                          </Typography>
                          {item.ProductionDate && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                display: 'block',
                                mt: 0.5,
                              }}
                            >
                              Production: {item.ProductionDate}
                            </Typography>
                          )}
                          {item.ExpirationDate && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? '#fbbf24' : '#f59e0b',
                                display: 'block',
                                mt: 0.25,
                              }}
                            >
                              Expires: {item.ExpirationDate}
                            </Typography>
                          )}
                        </TableCell>

                        {/* EXISTING: Quantity Column */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.Quantity || 'Not specified'}
                          </Typography>
                          {item.Packing && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                display: 'block',
                                mt: 0.5,
                              }}
                            >
                              Packing: {item.Packing}
                            </Typography>
                          )}
                        </TableCell>

                        {/* EXISTING: Origin Column */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.Origin || 'Not specified'}
                          </Typography>
                          {item.BatchNumber && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                display: 'block',
                                mt: 0.5,
                              }}
                            >
                              Batch: {item.BatchNumber}
                            </Typography>
                          )}
                        </TableCell>

                        {/* EXISTING: Value Column */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#10b981',
                            }}
                          >
                            {formatCurrency(
                              item.GoodsValue,
                              item.Currency || certificate.InvoiceCurrency
                            )}
                          </Typography>
                        </TableCell>

                        {/* EXISTING: Standard Ref Column */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.StandardRefNumber || 'Not specified'}
                          </Typography>
                        </TableCell>

                        {/* EXISTING: Brand Column */}
                        <TableCell
                          sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.Brand || 'Not specified'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Package size={48} color={isDarkMode ? '#64748b' : '#94a3b8'} />
                <Typography
                  variant="h6"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    mt: 2,
                    mb: 1,
                  }}
                >
                  No Items Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: isDarkMode ? '#64748b' : '#94a3b8' }}
                >
                  This certificate does not contain any items.
                </Typography>
              </Box>
            )}
          </TabPanel>

          {/* PDF Viewer Tab */}
          <TabPanel value={activeTab} index={5}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Eye size={20} color="#3b82f6" />
                Certificate PDF Preview
                {isDraft && (
                  <Chip
                    label="DRAFT VERSION"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      background:
                        'linear-gradient(135deg, #f59e0b20, #f59e0b10)',
                      color: '#f59e0b',
                      border: '1px solid #f59e0b30',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Typography>

              {isDraft && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    background: isDarkMode
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'rgba(59, 130, 246, 0.05)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                  }}
                >
                  <Typography variant="body2">
                    You are viewing the draft version of this certificate. The
                    PDF may show placeholder data until converted to production.
                  </Typography>
                </Alert>
              )}
            </Box>

            {/* PDF Viewer Component */}
            <Box
              sx={{
                border: isDarkMode
                  ? '1px solid rgba(148, 163, 184, 0.1)'
                  : '1px solid rgba(148, 163, 184, 0.2)',
                borderRadius: 2,
                overflow: 'hidden',
                background: isDarkMode
                  ? 'rgba(30, 41, 59, 0.6)'
                  : 'rgba(255, 255, 255, 0.8)',
              }}
            >
              {certificate.Id ? (
                <PDFViewer
                  certificateId={certificate.Id}
                  certificate={certificate}
                  isDarkMode={isDarkMode}
                  height="70vh"
                  showControls={true}
                  autoLoad={true}
                />
              ) : (
                <Box
                  sx={{
                    height: '70vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    p: 4,
                  }}
                >
                  <FileText
                    size={64}
                    color={isDarkMode ? '#64748b' : '#94a3b8'}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    PDF Not Available
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: isDarkMode ? '#64748b' : '#94a3b8' }}
                  >
                    Certificate PDF is being generated...
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value={activeTab} index={6}>
            {!isDraft ? (
              <DocumentsTab certificate={certificate} isDarkMode={isDarkMode} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',

                  textAlign: 'center',
                  color: isDarkMode ? '#94a3b8' : '#475569',
                  p: 4,
                  borderRadius: 2,
                  backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc',
                  boxShadow: isDarkMode ? '0 0 0' : '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Box sx={{ fontSize: 48, mb: 2 }}>📂</Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  No Uploaded Documents
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  This certificate is currently in draft mode. Documents will
                  appear here once uploaded.
                </Typography>

                <Alert severity="warning" sx={{ maxWidth: 400 }}>
                  Documents cannot be uploaded while in draft mode. Please
                  finalize the certificate first.
                </Alert>
              </Box>
            )}
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
        }}
      >
        <CustomButton
          variant="gradient"
          gradient="primary"
          onClick={handleDownload}
          startIcon={<Download size={16} />}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          Download PDF {isDraft && '(Draft)'}
        </CustomButton>
        <CustomButton variant="outlined" onClick={onClose}>
          Close
        </CustomButton>
      </DialogActions>
      {/* Draft to Production Modal */}
      <DraftToProductionModal
        open={convertModalOpen}
        onClose={handleCloseConvertModal}
        certificate={certificate}
        onConvert={handleModalConversion}
        isDarkMode={isDarkMode}
      />

      {/* Reissue Confirmation Dialog */}
      <Dialog
        open={reissueDialogOpen}
        onClose={handleCloseReissueDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RefreshCw size={24} color="#3b82f6" />
            <Typography
              variant="h6"
              sx={{
                ml: 1,
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              Reissue Certificate
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ color: isDarkMode ? '#e2e8f0' : '#374151', mb: 2 }}>
            Are you sure you want to reissue certificate <strong>{certificate?.CocNumber}</strong>?
          </Typography>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              color: isDarkMode ? '#93c5fd' : '#1e40af',
            }}
          >
            A new certificate will be created with all the same data. The new certificate will have a COC number in the format: <strong>{certificate?.CocNumber}-1</strong>
          </Alert>
          <Typography sx={{ color: isDarkMode ? '#cbd5e1' : '#64748b', fontSize: '0.875rem' }}>
            The new certificate will be created as a draft and can be edited before finalizing.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <CustomButton 
            variant="outlined" 
            onClick={handleCloseReissueDialog} 
            disabled={isReissuing}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="gradient"
            gradient="primary"
            onClick={handleConfirmReissue}
            disabled={isReissuing}
            startIcon={isReissuing ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
              },
            }}
          >
            {isReissuing ? 'Reissuing...' : 'Confirm Reissue'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ViewCertificateDialog;
